/* eslint-disable react-refresh/only-export-components */
import { type FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Doc } from "sharedb/lib/client";

import {
  INSTRUCTOR_SHARED_ACCESS_ID,
  LINKED_SECTION_SEEDING_ACCESS_ID,
} from "../../shared/access-id";
import type { BlockType, FieldType, PathType, WorksheetType } from "../../shared/types/worksheet";
import { DATA_PATH } from "../../shared/worksheets/constants";
import { Field } from "../components/Field";

import { t } from "i18n/i18n";
import { Modal, showModalWithProviders } from "mds/components/Modal";
import { PageIcon } from "mds/icons";
import { DocViewContext, type DocViewContextType } from "providers/DocViewProvider";

type ShowTemplateBlockModalProps = {
  doc: Doc<WorksheetType>;
  block: BlockType;
  docViewContextValue: DocViewContextType;
  versionType: "instructor" | "template";
};

const TemplateBlockModalBody: FC<ShowTemplateBlockModalProps> = ({
  doc,
  block,
  docViewContextValue,
  versionType,
}) => {
  const [blockHeight, setBlockHeight] = useState<number>(0);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      setBlockHeight(ref.current.clientHeight);
    }
  }, []);

  const instructorAccessIdPath = useMemo(
    () => [DATA_PATH, INSTRUCTOR_SHARED_ACCESS_ID] as PathType,
    [],
  );

  const blockIndex = useMemo(
    () => doc.data.b.findIndex((b) => b.id === block.id),
    [doc.data.b, block.id],
  );
  const fieldPaths = useMemo<PathType[]>(() => {
    if (blockIndex < 0) {
      return [];
    }
    return block.f.map((_, index) => ["b", String(blockIndex), "f", String(index)] as PathType);
  }, [block.f, blockIndex]);

  const modalContext = useMemo<DocViewContextType>(() => {
    return {
      ...docViewContextValue,
      readOnly: true,
      canInteractAsStudent: false,
      isAuthoringPage: false,
      showResponses: false,
      isFeaturing: false,
      accessId: versionType === "template" ? LINKED_SECTION_SEEDING_ACCESS_ID : "",
    };
  }, [docViewContextValue, versionType]);

  if (blockIndex < 0) {
    return null;
  }

  return (
    <DocViewContext.Provider value={modalContext}>
      <div className="max-h-[80vh] overflow-auto px-8 py-6" ref={ref}>
        <div className="page-block-width">
          {(block.f as FieldType[]).map((field, index) => {
            // Field internally keys based on accessId+field.id in some paths; keep this stable per render.
            return (
              <Field
                accessIdPath={instructorAccessIdPath}
                blockHeight={blockHeight}
                blockId={block.id}
                blockType={block.t}
                doc={doc}
                fieldPath={fieldPaths[index]}
                key={`${block.id}-${field.id}`}
                responseAccessIds={[]}
              />
            );
          })}
        </div>
      </div>
    </DocViewContext.Provider>
  );
};

export function showTemplateBlockModal(props: ShowTemplateBlockModalProps) {
  return showModalWithProviders((unmount) => {
    const header = (
      <div className="ml-8 flex flex-col gap-0.5 text-black-tint-20">
        <div className="text-s flex items-center gap-1 font-medium">
          <PageIcon />
          {t(`worksheet.template_block_modal.${props.versionType}.title`)}
        </div>
        <div className="text-s font-normal italic">
          {t(`worksheet.template_block_modal.${props.versionType}.subtitle`)}
        </div>
      </div>
    );

    return (
      <>
        <div className="modal-backdrop bg-black-tint-95/80 backdrop-blur-sm" />
        <Modal
          allowManualClose={false}
          className="w-[770px] max-w-[calc(100vw-32px)]"
          title={header}
          onClose={() => unmount()}
        >
          <TemplateBlockModalBody {...props} />
        </Modal>
      </>
    );
  });
}
